import './NotificationComponentConfig';
import { Box } from '@mui/material';
import React, { ReactNode, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { notificationsVar } from '../../../cache/cache';
import { NOTIFICATION_TEXT } from '../../../constants/notification';
import { ModalContext } from '../../../contexts/ModalContext';
import { NotificationContext } from '../../../contexts/NotificationsContext';
import type { AlertSeverity } from '../../../contexts/reducer/notificationReducer';
import AtomAlert from '../../atoms/AtomAlert';
import { styleConfigs } from '../../resources/styleConfigs';

interface INotificationComponentProps {
  title: string;
  description: string | ReactNode;
  severity?: AlertSeverity;
  variant?: AlertSeverity;
  autoClose?: boolean;
  autoHideDuration?: number;
  id: string | number;
  leftIcon?: ReactNode;
  linkText?: string;
  linkTo?: string;
  onLinkClick?: () => void;
}

const NotificationComponent: React.FC<INotificationComponentProps> = (props) => {
  const {
    title,
    description,
    autoClose,
    id,
    autoHideDuration = 5000,
    severity = 'error',
    variant,
    leftIcon,
    linkText,
    linkTo,
    onLinkClick,
  } = props;

  const effectiveSeverity = variant ?? severity;

  const navigate = useNavigate();

  const { t } = useTranslation('NotificationComponent');

  const translateTitle = title === NOTIFICATION_TEXT.errorTitle ? t(NOTIFICATION_TEXT.errorTitle) : title;
  const translateDescription =
    description === NOTIFICATION_TEXT.errorDescription ? t(NOTIFICATION_TEXT.errorDescription) : description;

  const { showNotification, setShowNotification, deleteNotification } = useContext(NotificationContext);

  const { isOpen, modalClose } = useContext(ModalContext);

  const handleLinkClick = (): void => {
    onLinkClick?.();

    if (!linkTo) {
      return;
    }

    if (isOpen) {
      modalClose();
    }
    navigate(linkTo);
  };

  const handleClose = (): void => {
    setShowNotification(!showNotification);
    deleteNotification(id);
    notificationsVar(notificationsVar().filter((n) => n.id !== id));
  };

  React.useEffect(() => {
    if (autoClose && !linkTo && !onLinkClick) {
      const timer = setTimeout(() => {
        setShowNotification(false);
        deleteNotification(id);
        notificationsVar(notificationsVar().filter((n) => n.id !== id));
      }, autoHideDuration);

      return () => clearTimeout(timer);
    }

    return undefined;
  }, [autoClose, autoHideDuration, id, linkTo, onLinkClick, setShowNotification, deleteNotification]);

  return (
    <Box
      data-testid="containerNotification"
      sx={{
        display: showNotification ? 'block' : 'none',
        width: {
          xs: '100%',
          sm: styleConfigs.dimensions.width323,
          md: styleConfigs.dimensions.width430,
        },
      }}
      aria-live="off"
    >
      <AtomAlert
        severity={effectiveSeverity}
        onClose={handleClose}
        title={translateTitle}
        role="alert"
        leftIcon={leftIcon}
        linkText={linkText}
        onLinkClick={linkText && (onLinkClick || linkTo) ? handleLinkClick : undefined}
      >
        {translateDescription}
      </AtomAlert>
    </Box>
  );
};

export default NotificationComponent;
